'use strict';

const qs = require('querystring');
const util = require('util');

const {StatusCodes, getReasonPhrase} = require('http-status-codes');

const request = require('@stubhub/request');

const {logGTMError, logGTMResponse} = require('./gtmLogger');

let cache;
let defaultOptions;
const ENFORCE_REQUEST_LOGGER = /^1|true$/i.test(process.env.ENFORCE_REQUEST_LOGGER);
const ENFORCE_CONSOLE_DEBUG = /^1|true$/i.test(process.env.REACT_APP_DEBUG_TO_CONSOLE);
const LOG_ERRORS_TO_GTM = /^1|true$/i.test(process.env.REACT_APP_LOG_ERRORS_TO_GTM);
const DEBUG_COOKIE = 'SH_DEBUG_CONSOLE';

const DEFAULT_TIMEOUT_MS = 5000;
const DEFAULT_TIMEOUT = parseInt(process.env.REACT_APP_REQUEST_TIMEOUT_MS, 10) || DEFAULT_TIMEOUT_MS;

const NS_PER_SEC = 1e3;
const NS_PER_SEC_INV = 1e-6;

/**
 *
 * @param {Object} requestCache sh-request-cache instantiated object
 */
exports.applyCache = function (requestCache) {
  cache = requestCache;
};

exports.applyDefaultOptions = function (opt) {
  defaultOptions = opt;
};

function doNothing() {
  return null;
}

function removeUndefinedShallow(obj) {
  if (obj && typeof obj === 'object') {
    const ret = {};
    for (const key in obj) {
      if (obj[key] !== undefined) {
        ret[key] = obj[key];
      }
    }

    return ret;
  }

  return obj;
}

/**
 * Merges the given options with the default options, prioritizing the given options.
 * @param {Object} options - The options to merge.
 * @param {Object} defaultOptions - The default options to merge with.
 * @returns {Object} - The merged options.
 */
function mergeOptions(options, defaultOptions) {
  if (!defaultOptions) {
    return options;
  }
  const opts = removeUndefinedShallow(options);
  const {headers} = opts;
  const d_headers = defaultOptions.headers;
  const mergedOptions = {...defaultOptions, ...opts};
  const mergedHeaders = {...d_headers, ...headers};
  const ret = Object.assign(mergedOptions, {headers: mergedHeaders});

  return ret;
}
exports.request = requestDefault;
exports.get = get;
exports.post = post;
exports.put = put;
exports.delete = delet;
exports.Request = request;
exports.requestHandler = requestHandler;

/**
 * Creates a request handler function that checks for backend errors and returns an error object if necessary.
 *
 * @param {function} done - The callback function to be called when the request is complete.
 * @param {object} requestOptions - The options for the request.
 * @returns {function} - The request handler function.
 */
function requestHandler(done, requestOptions) {
  if (typeof done === 'function') {
    return function onRequest(error, res, body) {
      if (!error && isBackendError(requestOptions, res)) {
        if (body !== null && typeof body === 'object') {
          const errorMessage = body.description || body.message || body.error;
          const errorContent =
            errorMessage !== null && typeof errorMessage === 'object' ? JSON.stringify(errorMessage) : errorMessage;
          error = new Error(errorContent);
        } else {
          error = new Error(body);
        }
        error.status = res && res.statusCode;
        error.body = body;
      }
      done(error, res, body);
    };
  }

  return done;
}

/**
 * Makes a request and caches the response if enabled.
 * @param {Object} requestOptions - The options for the request.
 * @param {boolean} requestOptions.enableCache - Whether or not to enable caching.
 * @returns {Promise} A promise that resolves with the response body.
 */
function requestCache(requestOptions) {
  const cacheQuery = requestOptions.enableCache ? cache.query(requestOptions) : false;

  return Promise.resolve(cacheQuery).then((cacheQueryResult) => {
    return new Promise((resolve, reject) => {
      let cacheKey;
      let currentCachedEntry;
      let revalidateCache;
      if (cacheQueryResult) {
        currentCachedEntry = cacheQueryResult.cachedEntry;
        ({cacheKey} = cacheQueryResult);
        ({revalidateCache} = cacheQueryResult);
        if (!revalidateCache && currentCachedEntry) {
          return resolve(currentCachedEntry.body);
        }
      }
      function callback(err, response, body) {
        if (err) {
          reject(err);
        } else {
          if (requestOptions.enableCache) {
            requestOptions.cachedEntry = currentCachedEntry;
            cache.set(cacheKey, response, requestOptions, revalidateCache);
          }

          resolve(body);
        }
      }
      const wrappedCallback = requestHandler(callback, requestOptions);
      requestDefault(requestOptions, wrappedCallback);
    });
  });
}

/**
 *
 * @param {Object} requestOptions request module compatible request options
 * @param {function} callback request module callback method
 */
function requestDefault(requestOptions, callback) {
  const startAt = process.hrtime();
  const method = (requestOptions.method && requestOptions.method.toLowerCase()) || 'get';
  const {logger} = requestOptions;
  const req = request[method](requestOptions, callback);
  req
    .on('error', (err) => {
      logError.call(req, logger, err, startAt, requestOptions);
    })
    .on('response', (response) => {
      logResponse.call(req, logger, response, startAt, requestOptions);
    });

  return req;
}

/**
 * Retrieves the value of a cookie by name.
 *
 * @param {string} name - The name of the cookie to retrieve.
 * @returns {?string} The value of the cookie, or null if the cookie does not exist.
 */
function getCookie(name) {
  if (typeof document !== 'undefined') {
    const match = document.cookie.match(new RegExp(`(^| )${name}=([^;]+)`));
    if (match) {
      return match[2];
    }
  }

  return null;
}

/**
 * Sends an HTTP request using the specified method and options.
 *
 * @param {Object} opts - The request options.
 * @param {string} opts.url - The URL to send the request to.
 * @param {string} [opts.method='GET'] - The HTTP method to use.
 * @param {Object} [opts.headers] - The HTTP headers to include in the request.
 * @param {string|Object} [opts.body] - The request body.
 * @param {Object} [opts.logger] - The logger to use for logging.
 * @returns {Promise} A Promise that resolves with the response data.
 * @throws {Error} If the logger is missing and ENFORCE_REQUEST_LOGGER is true.
 */
function verb(opts, method) {
  const requestOptions = getOpts(opts, method);
  if (!opts.logger && ENFORCE_REQUEST_LOGGER) {
    throw new Error('Missing logger in options');
  }
  const debugCookie = getCookie(DEBUG_COOKIE);
  const debugToConsole = debugCookie !== null ? debugCookie === 'true' : ENFORCE_CONSOLE_DEBUG;
  const logMethod = debugToConsole
    ? console
    : {
        log: doNothing,
        error: doNothing,
        info: doNothing,
        debug: doNothing,
        trace: doNothing,
        warn: doNothing,
      };
  requestOptions.logger = opts.logger || logMethod;
  if (cache && requestOptions.cache && /get/i.test(method)) {
    requestOptions.enableCache = true;
  }

  return requestCache(requestOptions);
}

function get(opts) {
  return verb(opts, 'GET');
}

function post(opts) {
  return verb(opts, 'POST');
}

function put(opts) {
  return verb(opts, 'PUT');
}

function delet(opts) {
  return verb(opts, 'DELETE');
}

function getOpts(opts, method) {
  opts = mergeOptions(opts, defaultOptions);
  const optstimeout = opts.timeout;
  const {host} = opts;
  const {path} = opts;
  const {body} = opts;

  const timeout = optstimeout === undefined ? DEFAULT_TIMEOUT : optstimeout;
  const headers = opts.headers || {};
  const {query} = opts;

  const requestOptions = {
    baseUrl: host,
    uri: path,
    body,
    method,
    timeout,
    headers,
    json: !!opts.json,
    acceptStatus: opts.acceptStatus,
    credentials: opts.credentials,
  };

  if (query && util.isObject(query)) {
    requestOptions.qs = query;
  }
  if (opts.maxAttempts) {
    requestOptions.maxAttempts = opts.maxAttempts;
  }
  if (opts.retryDelay) {
    requestOptions.retryDelay = opts.retryDelay;
  }
  if (opts.retryStrategy) {
    requestOptions.retryStrategy = opts.retryStrategy;
  }
  if (opts.formData) {
    requestOptions.formData = opts.formData;
  }
  if (opts.form) {
    requestOptions.form = opts.form;
  }
  if (Object.prototype.hasOwnProperty.call(opts, 'cache')) {
    // We want it if it's true/false
    requestOptions.cache = opts.cache;
  }
  if (opts.cacheMaxAge) {
    requestOptions.ttl = opts.cacheMaxAge;
  }
  if (opts.proxy) {
    requestOptions.proxy = opts.proxy;
  }
  if (opts.tunnel) {
    requestOptions.tunnel = opts.tunnel;
  }
  if (opts.delayStrategy) {
    requestOptions.delayStrategy = opts.delayStrategy;
  }
  if (opts.tag) {
    requestOptions.tag = opts.tag; // Used in logger to tag a request
  }

  return requestOptions;
}

function generateLogMetaForOutboundRequest(res, reqStartAt, requestOptions) {
  const meta = {
    direction: 'outbound',
  };
  /* istanbul ignore else */

  const resHeaders = res && res.headers;
  const request = this || (res && res.request);

  const reqUri = request && (request.uri || (request._req && request._req.uri));

  if (res) {
    // Meta['rs-status'] = res.statusCode;
    meta.status = res.statusCode;
  }

  const method = (request && request.method) || requestOptions.method;

  // Meta['sr-method'] = method;
  meta.http_method = method;

  /* istanbul ignore else */
  if (reqUri) {
    // Meta['sr-uri'] = reqUri.href;
    meta.url = reqUri.href;

    // Meta['sr-host'] = reqUri.host;
    meta.dest = reqUri.host;

    // Meta['sr-uri-stem'] = reqUri.pathname;
    meta.uri_path = reqUri.pathname;
  } else {
    let pathname = requestOptions.uri;
    pathname = `/${pathname.replace(/^\//, '')}`;

    let url = requestOptions.baseUrl + pathname;
    if (util.isObject(requestOptions.qs)) {
      url += `?${qs.stringify(requestOptions.qs)}`;
    }
    // Meta['sr-host'] = requestOptions.baseUrl;
    meta.dest = requestOptions.baseUrl;

    // Meta['sr-uri-stem'] = pathname;
    meta.uri_path = pathname;

    // Meta['sr-uri'] = url;
    meta.url = url;
  }

  /* istanbul ignore else */
  if (resHeaders) {
    // Meta['rs-bytes'] = resHeaders['content-length'];
    meta.bytes_in = resHeaders['content-length'];

    // Meta['rs(Content-Type)'] = resHeaders['content-type'];
    meta.http_content_type = resHeaders['content-type'];

    // Meta['rs(activityId)'] = resHeaders.activityid;
    meta.activityId = resHeaders.activityid;
  }

  const timing = res && res.timingPhases;

  /* istanbul ignore else */
  if (timing && timing.total) {
    // Total is the critical infor we need
    meta.timing_wait = timing.wait;
    meta.timing_dns = timing.dns;
    meta.timing_tcp = timing.tcp;
    meta.timing_firstByte = timing.firstByte;
    meta.timing_download = timing.timing_download;
    meta.response_time = timing.total;
  } else if (reqStartAt) {
    const diff = process.hrtime(reqStartAt);
    meta.response_time = diff[0] * NS_PER_SEC + diff[1] * NS_PER_SEC_INV;
  }

  if (requestOptions.tag) {
    meta.tag = requestOptions.tag;
  }

  if (requestOptions.proxy) {
    meta.proxy = 1; // Print out proxy is set or not
  }

  return meta;
}

function logError(logger, {code, status, syscall, address, port, message}, startAt, apiRequestOptions) {
  const statusCode = ['ECONNREFUSED', 'ECONNRESET', 'ETIMEDOUT', 'ESOCKETTIMEDOUT'].includes(code)
    ? StatusCodes.REQUEST_TIMEOUT
    : status;
  if (logger && typeof logger.error === 'function') {
    const meta = generateLogMetaForOutboundRequest.bind(this)(null, startAt, apiRequestOptions);
    meta.backend_error = true;
    if (statusCode === StatusCodes.REQUEST_TIMEOUT) {
      meta.status = StatusCodes.REQUEST_TIMEOUT;
      meta.network_err = true;
      meta.code = code;
      meta.syscall = syscall;
      meta.address = address;
      meta.port = port;
      meta.error = message;
    }
    logger.error(meta);
  }

  if (LOG_ERRORS_TO_GTM) {
    let reason = code;
    try {
      reason = getReasonPhrase(statusCode);
    } catch (e) {
      // Do nothing as the error is unknown
    }
    logGTMError({code: statusCode, name: reason, details: message}, apiRequestOptions);
  }
}

function logResponse(logger, resp, startAt, apiRequestOptions) {
  const backendError = isBackendError(apiRequestOptions, resp);
  if (logger && typeof logger.info === 'function') {
    const meta = generateLogMetaForOutboundRequest.bind(this)(resp, startAt, apiRequestOptions);
    meta.backend_error = backendError;
    logger.info(meta);
  }

  if (LOG_ERRORS_TO_GTM && backendError) {
    logGTMResponse(resp, apiRequestOptions);
  }
}

function isBackendError(apiRequestOptions, resp) {
  let backend_error;
  if (apiRequestOptions.acceptStatus && resp && resp.statusCode !== apiRequestOptions.acceptStatus) {
    backend_error = true;
  } else {
    backend_error = resp && resp.statusCode >= StatusCodes.BAD_REQUEST;
  }

  return backend_error;
}

if (process.env.NODE_ENV === 'test') {
  exports.mergeOptions = mergeOptions;
  exports.removeUndefined = removeUndefinedShallow;
}
